/**
 * How wide a cell is allowed to be, decided once instead of per table.
 *
 * `auto` (the browser default) sizes every column to its widest cell. That is
 * right when the cells are short and wrong the moment one is not: a single
 * hundred-character ingredient list dragged its column past a thousand pixels
 * and pushed the rest of the table off the screen.
 *
 * `fixed` sizes from the declared widths and lets cells wrap or truncate — but
 * it only helps when the columns FIT. A table with one column per product has
 * sixty of them, and dividing a screen sixty ways is not a table either.
 *
 * This used to be a decision the CALLER made — BOUNDED or UNBOUNDED, chosen
 * per report and then argued with by a separate wrap toggle. It is now one
 * three-state control the READER makes, because which one is right depends on
 * what they are doing rather than on which report they are in:
 *
 *   fit   — hold the declared widths, one line per cell, truncate the rest.
 *           Scanning forty rows for one number.
 *   wrap  — hold the declared widths, let cells wrap and rows grow taller.
 *           Reading what a cell actually says.
 *   long  — no declared widths at all: every column sizes to its content and
 *           the table scrolls sideways. Nothing is cut off anywhere, which is
 *           the only honest way to read a matrix with a column per product.
 */

/** A table holding its declared widths. */
export const BOUNDED = 'table-fixed w-full';

/** A table whose columns size to their content.
 *
 *  `w-full` as well, and the two are not in conflict: `table-auto` decides the
 *  RATIO of the columns from what is in them, and the width decides how much
 *  room they share out. Without it a matrix of eight glyph columns drew itself
 *  five hundred pixels wide in a two-thousand-pixel card and left the rest
 *  blank. A table whose content genuinely needs more still overflows and
 *  scrolls — a width on a table is a floor, not a ceiling, which is what makes
 *  long line long. */
export const UNBOUNDED = 'table-auto w-full';

/** The white ground a report's table sits on.
 *
 *  NOT ONLY A LOOK. The frozen first column paints an opaque background of its
 *  own so the table can scroll underneath it — which means it has to match
 *  what is behind the rest of the row, and there is nothing behind the rest of
 *  the row but this. A table dropped straight onto the page ground got a white
 *  first column against a grey one, a seam down the left of every row. Said
 *  once here rather than copied into each report, because the next table to be
 *  written would have copied the wrong thing. */
export const CARD =
  'shrink-0 rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800';

export type Density = 'fit' | 'wrap' | 'long';

/** In the order the toolbar offers them: tightest first. */
export const DENSITIES: Density[] = ['fit', 'wrap', 'long'];

export const DENSITY_LABEL: Record<Density, string> = {
  fit: '↔ fit',
  wrap: '⤶ wrap',
  long: '→ long line',
};

export const DENSITY_TITLE: Record<Density, string> = {
  fit: 'One line per cell, held to the declared widths - long values are cut off',
  wrap: 'Let cells wrap onto several lines: taller rows, nothing cut off',
  long: 'No width limit: every column sizes to its content and the table scrolls sideways',
};

/** `long` is the mode with no declared widths, so it is also the mode with no
 *  `<colgroup>`: a colgroup and `table-auto` argue, and the colgroup wins. */
export function declaresWidths(mode: Density): boolean {
  return mode !== 'long';
}

export function tableClass(mode: Density): string {
  return declaresWidths(mode) ? BOUNDED : UNBOUNDED;
}

/** What a cell wears in each mode. Wrapping cells align to the top, or a tall
 *  row leaves its short neighbours floating in the middle of nowhere. */
export function cellClass(mode: Density): string {
  if (mode === 'wrap') return 'whitespace-normal break-words align-top';
  // `long` still holds its line — it just has no width to be cut off by.
  if (mode === 'long') return 'whitespace-nowrap';
  return 'overflow-hidden text-ellipsis whitespace-nowrap';
}

/** What the FIRST cell of a row wears: the row's name. It wraps in `fit` too.
 *  One line per cell is for scanning numbers; cutting what a row IS short
 *  ("SimplePractice: Calendar sync locked behind expens") leaves a reader
 *  unable to tell rows apart, and on a table whose other cells already run
 *  to several lines it saved no height at all. `long` has no width, so it
 *  keeps its line. */
export function headCellClass(mode: Density): string {
  return mode === 'long' ? cellClass(mode) : 'whitespace-normal break-words align-top';
}

/** `<col>` elements from percentage weights. They need not sum to 100; what
 *  matters is their ratio to each other. */
export function cols(weights: number[]): { width: string }[] {
  const total = weights.reduce((a, b) => a + b, 0) || 1;
  return weights.map(w => ({ width: `${((w / total) * 100).toFixed(4)}%` }));
}

// Remembered per table, because the answer differs by table — a roster of long
// ingredient lists and a grid of doses do not want the same thing.
//
// And per ORIENTATION, because the same grid has different shapes each way
// round: thirteen attributes across fit a screen and sixty products do not, so
// the sheet reads as a fixed full-width grid one way and a scrolling one the
// other. Same reason the column choice and the sort are kept per orientation.

const MODE_KEY = 'jcseo.boardroom.mode';

function isDensity(v: string | null): v is Density {
  return v === 'fit' || v === 'wrap' || v === 'long';
}

function bucket(table: string, flipped: boolean): string {
  return `${MODE_KEY}.${table}${flipped ? '.flipped' : ''}`;
}

export function storedMode(table: string, fallback: Density = 'fit', flipped = false): Density {
  try {
    const raw = window.localStorage.getItem(bucket(table, flipped));
    return isDensity(raw) ? raw : fallback;
  } catch {
    return fallback;
  }
}

export function rememberMode(table: string, mode: Density, flipped = false): void {
  try {
    window.localStorage.setItem(bucket(table, flipped), mode);
  } catch {
    /* private mode: the default is fine */
  }
}
